import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatTableModule} from '@angular/material/table';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatChipsModule} from '@angular/material/chips';
import {User} from '../api/interfaces/user.interface';
import {MatAutocompleteModule, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {catchError, concatMap, debounceTime, map, Observable, of, startWith, switchMap} from 'rxjs';
import {UserService} from '../api/services/user.service';
import {MapService} from '../api/services/map.service';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDivider} from '@angular/material/divider';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MapUserRoles} from '../acl/map-user-roles.enum';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {SelectInputComponent} from '../shared/forms/select-input/select-input.component';
import {StoreService} from '../api/services/store.service';
import {PackageService} from '../api/services/package.service';
import {VisibilityWarningDialogComponent} from './visibility-warning.dialog';
import {HasMapPermissionDirective} from '../packages/has-map-permission.directive';
import {get, isEmpty} from 'lodash';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {GraphMap} from '../api/interfaces/graph-node.interface';
import {RoleIcons} from '../acl/map-role.service';
import {UserInitialsBadgeComponent} from "../shared/components/user-menu/user-badge.component";
import {
  MaporiumAccordionItemComponent
} from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {HasPackageDirective} from '../packages/has-package.directive';
import {PACKAGE_NAMES} from '@maporium-workspace/shared';

@Component({
  selector: 'maporium-share-map-dialog',
  styles: [`
    /* MAP-1581: the role dropdown sits inside a person row, so it takes only the
       width its content needs rather than the grid column it would otherwise fill. */
    .role-select {
      min-width: 132px;
    }

    ::ng-deep {

    .share-map-form {
      --mdc-theme-primary: #000000;
      .mdc-floating-label {
        color:  #000000 !important;
      }
      .mat-mdc-text-field-wrapper {
        height: auto;
      }
      .mat-mdc-form-field-infix {
        min-height: unset;
        padding-top: 6px !important;
        padding-bottom: 6px !important;
      }
    }


    }
  `],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <mat-icon class="material-symbols-rounded">person_add</mat-icon>
        <span style="padding-left: 4px">{{ 'SHARE_DIALOG.TITLE' | translate }}</span>
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close
            matTooltip="{{ 'SHARE_DIALOG.CLOSE_TOOLTIP' | translate }}"></span>
    </div>

    <div mat-dialog-content class="p-3 px-4">
      <cdk-accordion [multi]="true" class="accordion">
        <!-- People: add-users field + the users the smap is shared with -->
        <maporium-accordion-item
          [title]="'SHARE_DIALOG.PEOPLE' | translate"
          [sections]="addedEmails.length">

          <!-- Add Users -->
          <form class="share-map-form mb-3">
            <mat-form-field class="w-100" appearance="outline" subscriptSizing="dynamic">
              <input #userInput
                     matInput
                     [formControl]="userCtrl"
                     [matAutocomplete]="auto"
                     [placeholder]="'SHARE_DIALOG.SEARCH_PLACEHOLDER' | translate"
                     (keydown.enter)="$event.preventDefault()">
              <mat-autocomplete #auto="matAutocomplete"
                                class="compact-autocomplete-panel"
                                [displayWith]="displayUser"
                                (optionSelected)="selected($event)">
                <mat-option *ngFor="let user of filteredUsers | async" [value]="user">
                  <app-user-initials-badge class="me-2"
                                           [user]="user"
                                           [size]="28"
                                           [tooltip]="getUserTooltip(user)"
                                           tooltipPosition="right"></app-user-initials-badge>
                  {{ getUserDisplay(user) }}
                </mat-option>
              </mat-autocomplete>
            </mat-form-field>
          </form>

          <div *ngFor="let email of addedEmails; let last = last" class="my-2">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <app-user-initials-badge *ngIf="getUserByEmail(email) as user"
                                         class="me-2"
                                         [user]="user"
                                         [size]="28"
                                         [tooltip]="getUserTooltip(user)"
                                         tooltipPosition="right"></app-user-initials-badge>
                {{ getUserName(email) }} &lt;{{ email }}&gt;
              </div>
              <div class="d-flex align-items-center">
                <!-- MAP-1581: one dropdown carrying icon AND label, like the
                     access dropdown, instead of an icon button plus a loose
                     label plus a separate menu. -->
                <app-select-input class="role-select"
                                  [formControl]="roleControl(email)"
                                  [options]="roleOptions"
                                  [isMaporiumIcon]="true"
                                  [dropdownColSize]="12"
                                  [noMargin]="true">
                </app-select-input>

                <!-- Delete Button -->
                <button mat-icon-button (click)="remove(email)"
                        matTooltip="{{ 'SHARE_DIALOG.REMOVE_USER_TOOLTIP' | translate }}">
                  <mat-icon fontSet="material-symbols-rounded">delete</mat-icon>
                </button>
              </div>
            </div>
            <mat-divider *ngIf="!last"></mat-divider>
          </div>

        </maporium-accordion-item>

        <!-- Map URL Section -->
        <maporium-accordion-item
          [title]="'SHARE_DIALOG.ACCORDION.ACCESS' | translate">
          <div style="background-color: #e7f1fa; border-radius: 8px; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; gap: 8px;">
            <span style="color: #3d85c6; font-size: 14px; word-break: break-all;">{{ mapUrl }}</span>
            <button mat-icon-button
                    [cdkCopyToClipboard]="mapUrl"
                    matTooltip="{{ 'SHARE_DIALOG.COPY_MAP_LINK_TOOLTIP' | translate }}"
                    (click)="copyMapLink()"
                    style="flex-shrink: 0; color: #3d85c6;">
              <mat-icon fontSet="material-symbols-rounded" style="color: #3d85c6;">content_copy</mat-icon>
            </button>
          </div>
          <ng-container *hasMapPermission="'manage'">
            <app-select-input
              [labelColSize]="8"
              [dropdownColSize]="4"
              [label]="'EMBED_DIALOG.ACCESS.LEVEL' | translate"
              [formControl]="accessControl"
              [options]="accessOptions"
              (selectionChange)="onAccessChange($event)">
            </app-select-input>
            @if (accessControl.value === 'public') {
              <small class="mt-1">{{ "SHARE_DIALOG.ACCESS.PUBLIC_DESC" | translate }}</small>
            } @else {
              <small class="mt-1">{{ "SHARE_DIALOG.ACCESS.PRIVATE_DESC" | translate }}</small>
            }
          </ng-container>
        </maporium-accordion-item>
      </cdk-accordion>

    </div>

    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close>{{ 'SHARE_DIALOG.ACTIONS.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary" (click)="update()"
              *ngIf="hasChanges">{{ 'SHARE_DIALOG.ACTIONS.UPDATE' | translate }}
      </button>
    </div>
  `,
  standalone: true,
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatTableModule,
    CommonModule,
    ReactiveFormsModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatDivider,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    CdkCopyToClipboard,
    TranslatePipe,
    SelectInputComponent,
    HasMapPermissionDirective,
    UserInitialsBadgeComponent,
    MaporiumAccordionItemComponent,
    CdkAccordion,
    HasPackageDirective
  ]
})
export class ShareMapDialogComponent {
  userCtrl = new FormControl('');
  filteredUsers: Observable<User[]>;
  addedEmails: string[] = [];
  sharedWithUsers: User[] = [];
  hasChanges = false;
  currentEmail = '';
  /** MAP-1581: icon + label in one dropdown, mirroring accessOptions. */
  roleOptions: { key: string, value: string, icon: string, tooltipText: string }[] = [];
  private readonly roleControls = new Map<string, FormControl>();
  userRoles: { [email: string]: MapUserRoles } = {};
  mapUrl = '';
  accessControl = new FormControl('private');
  accessOptions: { key: string, value: string, icon: string }[] = [];

  private initialIsPublic = false;
  private initialAllowEmbedding = false;
  private initialIsPublished = false;
  // True only once the user explicitly picks a value in the access dropdown.
  // Sharing/adding users must never touch visibility, so publish/unpublish is
  // gated on this flag — not just on a value comparison that could drift.
  private accessChangedByUser = false;

  roleIcons = RoleIcons;
  //@ts-ignore
  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;

  constructor(private userService: UserService,
              private mapService: MapService,
              private storeService: StoreService,
              private dialogRef: MatDialogRef<ShareMapDialogComponent>,
              private dialog: MatDialog,
              public snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) private data: { map: GraphMap },
              private translate: TranslateService,
              private packageService: PackageService) {
    this.mapUrl = window.location.href;
    // Initialize sharing form, prefer fresh map data but fallback to passed-in map for roles
    this.mapService.findByIdWithRelation(data.map.id).subscribe((map) => {
      this.initialIsPublic = map.isPublic ?? false;
      this.initialAllowEmbedding = map.allowWebsiteEmbedding ?? false;
      this.accessControl.patchValue(map.isPublic ? 'public' : 'private', { emitEvent: false });

      // Check if map is published
      this.storeService.isPublished(map.id).subscribe(result => {
        this.initialIsPublished = result.isPublished;
      });

      if (map.sharedWith) {
        this.addedEmails = map.sharedWith.map(user => user.email) as string[];
        // Use userRoles from fetched map if available, otherwise fallback to roles from input data.map
        const rolesArray = (map.userRoles && map.userRoles.length > 0)
          ? map.userRoles
          : (data.map.userRoles || []);
        this.sharedWithUsers = map.sharedWith.map(user => {
          const roleEntry = rolesArray.find(r => r.user.id === user.id);
          return {
            ...user,
            role: roleEntry ? roleEntry.role.toUpperCase() : 'VIEWER'
          };
        });
      }
    });

    const canGrantWriteRoles = [
      PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer
    ].some((name) => this.packageService.hasPackage(name));
    // Each role keeps the explanation the old menu showed on hover.
    const role = (key: string) => ({
      key,
      value: this.translate.instant(`SHARE_DIALOG.ROLES.${key}`),
      icon: `icon-smap-role-${key.toLowerCase()}`,
      tooltipText: this.translate.instant(`SHARE_DIALOG.ROLE_DESC.${key}`)
    });
    this.roleOptions = [
      role('VIEWER'),
      role('COMMENTER'),
      ...(canGrantWriteRoles ? [role('EDITOR'), role('MANAGER')] : [])
    ];

    this.accessOptions = [
      { key: 'public', value: this.translate.instant('EMBED_DIALOG.ACCESS.PUBLIC'), icon: 'public' },
      { key: 'private', value: this.translate.instant('EMBED_DIALOG.ACCESS.PRIVATE'), icon: 'lock' }
    ];

    // Server-side, privacy-gated search: the backend only returns the current
    // user's share connections (people they've shared a smap with, or who have
    // shared one with them) matching the typed name/email, plus an exact
    // full-email match. The full user list is never shipped to the client.
    this.filteredUsers = this.userCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(250),
      map(value => (typeof value === 'string' ? value : '')),
      switchMap(term =>
        this.userService.getAvailableUsersForMapSharing(this.data.map.id, term.trim()).pipe(
          map(users => users.filter(u => !this.addedEmails.includes(u.email as string))),
          catchError(() => of([] as User[]))
        )
      )
    );
  }

  getUserDisplay(user: User): string {
    const name = `${user.name || ''}${!isEmpty(user.lastName) ? ' ' + user.lastName : ''}`.trim();
    return name ? `${name} <${user.email}>` : (user.email || '');
  }

  // Rich hover tooltip matching the smap assistant bubble badges:
  // "<first> <last> (<org>): <bio>".
  getUserTooltip(user?: User): string {
    if (!user) return '';
    const fullName = `${user.name || ''} ${user.lastName || ''}`.trim();
    const org = user.organization ? ` (${user.organization})` : '';
    const bio = user.biography ? `: ${user.biography}` : '';
    return `${fullName}${org}${bio}`.trim();
  }

  displayUser = (user: User | string | null): string => {
    if (!user) return '';
    if (typeof user === 'string') return user;
    return this.getUserDisplay(user);
  };

  onAccessChange(option: { key: string, value: string, icon?: string }) {
    this.hasChanges = true;
    this.accessChangedByUser = true;
    // Only warn when the user explicitly switches to private while embedding or
    // publishing is active, since that would revoke access for public viewers.
    if (option.key === 'private' && (this.initialAllowEmbedding || this.initialIsPublished)) {
      let usage: 'embedding' | 'publishing' | 'publishing and embedding';
      if (this.initialIsPublished && this.initialAllowEmbedding) {
        usage = 'publishing and embedding';
      } else if (this.initialIsPublished) {
        usage = 'publishing';
      } else {
        usage = 'embedding';
      }
      this.showVisibilityWarning(usage);
    }
  }

  private showVisibilityWarning(usage: 'embedding' | 'publishing' | 'publishing and embedding') {
    this.dialog.open(VisibilityWarningDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        mapName: this.data.map.name,
        usage
      }
    });
  }

  roleIcon(email: string): string {
    const user = this.sharedWithUsers.find(user => user.email === email);
    const role = user?.role?.toUpperCase() || get(this.userRoles, email) || 'VIEWER';
    return this.roleIcons[role] || 'visibility';
  }

  roleFromIcon(icon: string): string {
    const role = Object.keys(this.roleIcons).find(key => this.roleIcons[key] === icon) || 'VIEWER';
    return role.toLowerCase();
  }

  /**
   * One control per person, created on first render and reused after, so the
   * dropdown keeps its identity across change detection.
   */
  roleControl(email: string): FormControl {
    const existing = this.roleControls.get(email);
    if (existing) {
      return existing;
    }
    const control = new FormControl(this.getRole(email));
    control.valueChanges.subscribe((role) => {
      if (role) {
        this.selectRole(email, role);
      }
    });
    this.roleControls.set(email, control);
    return control;
  }

  getRole(email: string): string {
    const user = this.sharedWithUsers.find(user => user.email === email);
    return user?.role || 'VIEWER';
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const user: User = event.option.value;
    this.userInput.nativeElement.value = '';
    this.userCtrl.setValue(null);

    if (!user?.email || this.addedEmails.includes(user.email)) return;

    this.addedEmails.push(user.email);
    this.sharedWithUsers.push({
      ...user,
      role: (user.role || 'viewer').toString().toUpperCase()
    });
    this.hasChanges = true;
  }

  getUserName(email: string): string {
    if (!email) return '';
    const user = this.sharedWithUsers.find(user => user.email === email);
    if (!user) return '';
    return `${user.name}${!isEmpty(user.lastName) ? ' ' + user.lastName : ''}` as string;
  }

  getUserByEmail(email: string): User | undefined {
    return this.sharedWithUsers.find(user => user?.email === email);
  }

  selectRole(email: string, role: string) {
    this.userRoles[email] = role as MapUserRoles;
    // Look for user in sharedWithUsers and update role
    const user = this.sharedWithUsers.find(user => user.email === email);
    if (user) {
      user.role = role;
    }
    this.hasChanges = true;
  }

  remove(email: string): void {
    const role = this.getRole(email);
    const userName = this.getUserName(email);
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('SHARE_DIALOG.REMOVE_USER_CONFIRMATION_TITLE'),
        message: this.translate.instant('SHARE_DIALOG.REMOVE_USER_CONFIRMATION_MESSAGE', {
          user_name: userName,
          role_name: role.toLowerCase()
        })
      }
    });

    ref.afterClosed().subscribe((result) => {
      if (result) {
        const index = this.addedEmails.indexOf(email);

        if (index >= 0) {
          const user = this.sharedWithUsers.find(user => user.email === email);
          if (user) {
            user.role = 'viewer';
          }

          this.addedEmails.splice(index, 1);
          this.sharedWithUsers.splice(index, 1); // Remove from shared users
        }

        this.hasChanges = true;
        this.userCtrl.reset();
      }
    });
  }



  update() {
    let usersToShare = this.sharedWithUsers
      .filter(user => !!user.email && this.addedEmails.includes(user.email as string))
      .map(user => ({
        id: user.id,
        role: user.role?.toLowerCase() || 'viewer'
      }));

    if (usersToShare.length === 0) {
      usersToShare = [];
    }

    const payload = usersToShare.map(user => {
      return {
        id: user.id,
        role: user.role.toLowerCase() as MapUserRoles
      };
    });

    // Only change visibility when the user explicitly switched the access dropdown.
    // Sharing alone must never flip an already-public/embedded smap to private.
    const shouldBePublic = this.accessControl.value === 'public';
    let access$: Observable<unknown> = of(null);
    if (this.accessChangedByUser && shouldBePublic !== this.initialIsPublic) {
      if (shouldBePublic) {
        access$ = this.publish(this.data.map.id);
      } else {
        // Going private must deactivate every form of public exposure: closing
        // public access turns off embedding, and a store-published smap must
        // also be unpublished (it can't remain in the public store while private).
        access$ = this.unpublish(this.data.map.id).pipe(
          concatMap(() => this.initialIsPublished
            ? this.storeService.unpublishMap(this.data.map.id)
            : of(null))
        );
      }
    }

    access$.pipe(
      concatMap(() => this.mapService.shareMap(this.data.map.id, { users: payload }))
    ).subscribe((res) => this.dialogRef.close(res));
  }

  copyMapLink() {
    this.snackBar.open(this.translate.instant('SHARE_DIALOG.LINK_COPIED'), '', { duration: 2000 });
  }

  private publish(mapId: string) {
    return this.mapService.openPublicAccessToMap(mapId);
  }

  private unpublish(mapId: string) {
    return this.mapService.closePublicAccessToMap(mapId);
  }

  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;
}
